// Adapters from raw EDOS TestSpecification rows → the shapes expected by
// TestDetail.js and PackageDetail.js. The API returns one row of
// `test_specifications`; both pages render the same underlying record but with
// different field names + extra UI bits.

const truthy = (v) =>
  v === true ||
  v === 1 ||
  v === '1' ||
  v === 'Y' ||
  v === 'y' ||
  v === 'Yes' ||
  v === 'yes';

const splitToList = (s) =>
  s ? String(s).split(',').map((x) => x.trim()).filter(Boolean) : [];

const ICONS = ['🧪', '💧', '❤', '🦠', '🧬', '🩺', '🧠', '🩸'];
const pickIcon = (id) => ICONS[Math.abs(Number(id) || 0) % ICONS.length];

// ───── Used by TestDetail ─────
export const mapApiToTestDetail = (api) => {
  if (!api) return null;
  const price = Number(api.mrp) || 0;
  const original = price; // EDOS only exposes one consumer price; use it for both
  const params = splitToList(api.parameters);

  return {
    id: api.id,
    icon: pickIcon(api.id),
    tag: api.specialisation || api.test_type || null,
    name: api.test_name,
    code: api.test_code || '—',
    category: api.departments || api.test_type || 'General',
    paramCount: params.length || 1,
    price,
    original,
    method: api.test_methodology || '—',
    department: api.departments || '—',
    disease: api.diseases || '—',
    specialty: api.doctor_specialty || '—',
    gender: api.gender || 'M / F',
    age: api.age_range || 'All',
    homeCol: truthy(api.home_collection),
    walkIn: truthy(api.walk_in),
    fasting: api.fasting && api.fasting.toLowerCase() !== 'none',
    tat: api.report_tat || '—',
    sampleType: api.sample_type || '—',
    sampleVol: api.sample_volume || '—',
    container: api.sample_container || '—',
    schedule: api.test_schedule || 'M,Tu,W,Th,F,S',
    cutoff: api.sample_receipt_cutoff || '—',
    parameters: params,
    significance: api.clinical_aid || '',
    instructions: api.special_instructions || '',
    faqs: [],
  };
};

// ───── Used by PackageDetail ─────
export const mapApiToPackageDetail = (api) => {
  if (!api) return null;
  const price = Number(api.l2l_price) || Number(api.mrp) || 0;
  const mrp = Number(api.mrp) || price;
  const savePct = mrp > price && mrp > 0
    ? Math.round(((mrp - price) / mrp) * 100)
    : 0;
  const params = splitToList(api.parameters);

  // Group all params under one "Tests Included" group for now.
  // PackageDetail iterates `g.tests`, not `g.items`.
  const groups = params.length
    ? [{ name: api.test_name || 'Included Tests', tests: params }]
    : [];

  return {
    id: api.id,
    icon: pickIcon(api.id),
    tag: api.specialisation || api.test_type || null,
    name: api.test_name,
    code: api.test_code || '—',
    paramCount: params.length || 1,
    tat: api.report_tat || '—',
    price,
    original: mrp,
    savePercent: `${savePct}%`,
    gender: api.gender || 'M / F',
    fasting: api.fasting && api.fasting.toLowerCase() !== 'none',
    description: api.clinical_aid || '',
    ideal: api.doctor_specialty || '',
    whyNeed: api.special_instructions || '',
    prep: api.fasting && api.fasting.toLowerCase() !== 'none'
      ? `Fasting required: ${api.fasting}`
      : 'No special preparation required.',
    groups,
    faqs: [],
  };
};
